import { useState, useEffect, useCallback, useRef } from 'react'
import { cancellationsApi } from '@/services/api/cancellations'
import type { CancellationAdminResponse } from '@/types/admin5'
import { extractErrorMessage } from '@/utils/errorHandler'
import { useToast } from '@/components/ui/ToastProvider'
import { getStatusColor, getStatusLabel } from '@/utils/statusHelpers'
import { RefreshCw, Check, X } from 'lucide-react'

function formatRelative(iso: string): string {
  const diff = new Date(iso).getTime() - Date.now()
  const absMin = Math.abs(diff) / 60000
  if (absMin < 60) return diff > 0 ? `in ${Math.round(absMin)} Min.` : `vor ${Math.round(absMin)} Min.`
  const absH = absMin / 60
  if (absH < 24) return diff > 0 ? `in ${Math.round(absH)} Std.` : `vor ${Math.round(absH)} Std.`
  return diff > 0 ? `in ${Math.round(absH / 24)} Tg.` : `vor ${Math.round(absH / 24)} Tg.`
}

type FilterTab = 'all' | 'pending' | 'accepted' | 'declined' | 'expired'

export function CancellationManagementPage() {
  const { showToast } = useToast()
  const [stornos, setStornos] = useState<CancellationAdminResponse[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState<FilterTab>('all')
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [busyId, setBusyId] = useState<string | null>(null)
  const initRef = useRef(false)

  const fetchStornos = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true); else setLoading(true)
    try {
      const res = await cancellationsApi.listAllStornos()
      setStornos(res)
      setError(null)
    } catch (err) {
      setError(extractErrorMessage(err, { loadError: 'Stornos konnten nicht geladen werden.' }))
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [])

  useEffect(() => {
    if (initRef.current) { fetchStornos(true) } else { initRef.current = true; fetchStornos() }
  }, [fetchStornos])

  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') fetchStornos(true)
    }, 30000)
    return () => clearInterval(id)
  }, [fetchStornos])

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300)
    return () => clearTimeout(t)
  }, [search])

  const handleOverride = async (cancellationId: string, action: 'accept' | 'decline') => {
    const verb = action === 'accept' ? 'akzeptieren' : 'ablehnen'
    if (!window.confirm(`Storno als Admin ${verb}? Dies überspringt die Lehrer-Bestätigung.`)) return
    setBusyId(cancellationId)
    try {
      const res = await cancellationsApi.adminOverride(cancellationId, action)
      showToast({ message: res.message || `Storno ${action === 'accept' ? 'akzeptiert' : 'abgelehnt'}.`, type: res.success ? 'success' : 'info' })
      fetchStornos(true)
    } catch (err) {
      showToast({ message: extractErrorMessage(err, { loadError: `Konnte Storno nicht ${verb}.` }), type: 'error' })
    } finally { setBusyId(null) }
  }

  const filtered = stornos.filter(s => {
    if (filter !== 'all' && s.status !== filter && !(filter === 'pending' && s.status === 'partially_accepted')) return false
    if (debouncedSearch) {
      const q = debouncedSearch.toLowerCase()
      return (s.reason?.toLowerCase().includes(q)
        || s.initiating_role?.toLowerCase().includes(q)
        || s.requesting_teacher_name?.toLowerCase().includes(q)
        || s.responding_teacher_name?.toLowerCase().includes(q))
    }
    return true
  })

  const pendingCount = stornos.filter(s => s.status === 'pending' || s.status === 'partially_accepted').length
  const acceptedCount = stornos.filter(s => s.status === 'accepted').length
  const declinedCount = stornos.filter(s => s.status === 'declined').length
  const expiredCount = stornos.filter(s => s.status === 'expired').length

  if (loading) return <div className="p-6 text-gray-500">Laden...</div>

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Stornierungs-Verwaltung</h1>
          <p className="text-sm text-gray-500 mt-1">Schulweite Übersicht aller Stornos · Admin kann übersteuern</p>
        </div>
        <div className="flex items-center gap-2">
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Suche Name / Grund…"
            className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 w-48" />
          <button onClick={() => fetchStornos(true)} disabled={refreshing}
            className="flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Aktualisieren
          </button>
        </div>
      </div>

      {error && <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">{error}</div>}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-xs text-gray-500">Offen</div>
          <div className="text-2xl font-bold text-amber-600 mt-1">{pendingCount}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-xs text-gray-500">Akzeptiert</div>
          <div className="text-2xl font-bold text-green-600 mt-1">{acceptedCount}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-xs text-gray-500">Abgelehnt</div>
          <div className="text-2xl font-bold text-red-600 mt-1">{declinedCount}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-xs text-gray-500">Abgelaufen</div>
          <div className="text-2xl font-bold text-gray-500 mt-1">{expiredCount}</div>
        </div>
      </div>

      <div className="flex gap-1 mb-4 flex-wrap">
        {([
          { key: 'all', label: `Alle (${stornos.length})` },
          { key: 'pending', label: `Offen (${pendingCount})` },
          { key: 'accepted', label: `Akzeptiert (${acceptedCount})` },
          { key: 'declined', label: `Abgelehnt (${declinedCount})` },
          { key: 'expired', label: `Abgelaufen (${expiredCount})` },
        ] as Array<{ key: FilterTab; label: string }>).map(tab => (
          <button key={tab.key} onClick={() => setFilter(tab.key)}
            className={`px-3 py-1.5 text-xs rounded-md ${filter === tab.key ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-500 hover:bg-gray-100'}`}>
            {tab.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white rounded-lg border p-8 text-center text-gray-500">
          <p>Keine Stornierungsanfragen</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200 text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-4 py-2.5 text-left font-medium">Lehrer</th>
                  <th className="px-4 py-2.5 text-left font-medium">Rolle</th>
                  <th className="px-4 py-2.5 text-left font-medium">Grund</th>
                  <th className="px-4 py-2.5 text-left font-medium">Angefragt</th>
                  <th className="px-4 py-2.5 text-left font-medium">Items</th>
                  <th className="px-4 py-2.5 text-left font-medium">Status</th>
                  <th className="px-4 py-2.5 text-right font-medium">Aktionen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map(s => {
                  const isPending = s.status === 'pending' || s.status === 'partially_accepted'
                  const pendingItems = s.items.filter(i => i.status === 'pending')
                  return (
                    <tr key={s.id} className={`hover:bg-gray-50 ${s.status === 'accepted' ? 'bg-green-50/30' : ''}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{s.requesting_teacher_name || 'Unbekannt'}</div>
                        {s.responding_teacher_name && (
                          <div className="text-xs text-gray-500">→ {s.responding_teacher_name}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-gray-700">{s.initiating_role || '—'}</span>
                        {s.re_dispatch && <span className="ml-1 text-xs text-blue-500">↻</span>}
                      </td>
                      <td className="px-4 py-3 text-gray-700 max-w-[200px] truncate">{s.reason || '—'}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{formatRelative(s.created_at)}</td>
                      <td className="px-4 py-3">
                        <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">
                          {pendingItems.length}/{s.items.length} offen
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 text-xs rounded-full inline-flex items-center gap-1 ${getStatusColor(s.status)}`}>
                          {isPending && <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />}
                          {getStatusLabel(s.status)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {isPending ? (
                          <div className="flex items-center justify-end gap-1.5">
                            <button onClick={() => handleOverride(s.id, 'accept')} disabled={busyId === s.id}
                              className="text-xs text-green-700 border border-green-600 hover:bg-green-50 px-2 py-1 rounded flex items-center gap-1 disabled:opacity-50">
                              <Check className="w-3 h-3" /> Genehmigen
                            </button>
                            <button onClick={() => handleOverride(s.id, 'decline')} disabled={busyId === s.id}
                              className="text-xs text-red-700 border border-red-600 hover:bg-red-50 px-2 py-1 rounded flex items-center gap-1 disabled:opacity-50">
                              <X className="w-3 h-3" /> Ablehnen
                            </button>
                          </div>
                        ) : <span className="text-xs text-gray-400">—</span>}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
